import type {
  ConversationStatus,
  LeadStatus,
  Message,
} from '@conversia/shared-types';
import type { Paginated, ConversationWithRelations } from '@/lib/types';
import { api } from './client';
import { useAuthStore } from '@/store/auth.store';

/** Endpoints de conversaciones. Prefijo: /api/conversations */

export type ConversationTypeFilter = 'all' | 'individual' | 'group';

export interface ListConversationsParams {
  status?: ConversationStatus;
  leadStatus?: LeadStatus;
  tipoContacto?: string;
  channelId?: string;
  agentId?: string;
  assignedUserId?: string;
  search?: string;
  type?: ConversationTypeFilter;
  skip?: number;
  take?: number;
}

export const listConversations = (params: ListConversationsParams = {}) =>
  api.get<Paginated<ConversationWithRelations>>('/api/conversations', { query: params });

export interface CambioTemperatura {
  de: string | null;
  a: string | null;
  motivo: string;
  fecha: string;
}

export const listTemperaturas = (id: string) =>
  api.get<CambioTemperatura[]>(`/api/conversations/${id}/temperaturas`);

export const getConversation = (id: string) =>
  api.get<ConversationWithRelations>(`/api/conversations/${id}`);

export const listMessages = (id: string, params: { skip?: number; take?: number } = {}) =>
  api.get<Paginated<Message>>(`/api/conversations/${id}/messages`, { query: params });

export const sendMessage = (id: string, content: string, mediaUrl?: string) =>
  api.post<Message>(`/api/conversations/${id}/messages`, { content, mediaUrl });

export const updateStatus = (id: string, status: ConversationStatus) =>
  api.patch<ConversationWithRelations>(`/api/conversations/${id}/status`, { status });

export const updateLeadStatus = (id: string, leadStatus: LeadStatus | null) =>
  api.patch<ConversationWithRelations>(`/api/conversations/${id}/lead-status`, { leadStatus });

export const assignAgent = (id: string, agentId: string | null) =>
  api.post<ConversationWithRelations>(`/api/conversations/${id}/assign-agent`, { agentId });

export const assignUser = (id: string, userId: string | null) =>
  api.post<ConversationWithRelations>(`/api/conversations/${id}/assign-user`, { userId });

export const markRead = (id: string) =>
  api.post<{ success: boolean }>(`/api/conversations/${id}/read`);

export const addTag = (id: string, tagId: string) =>
  api.post<{ success: boolean }>(`/api/conversations/${id}/tags`, { tagId });

export const removeTag = (id: string, tagId: string) =>
  api.del<{ success: boolean }>(`/api/conversations/${id}/tags/${tagId}`);

/**
 * Export XLSX del CRM (F3, ADMIN). Descarga el archivo con el token del store
 * (no se puede usar un <a href> plano: la ruta exige Authorization). El nombre
 * viene del servidor — sufijo PARCIAL si el tope de 50.000 filas recortó.
 */
export async function downloadCrmExport(): Promise<void> {
  const base = (import.meta.env.VITE_API_URL as string | undefined) ?? '';
  const token = useAuthStore.getState().accessToken;
  const res = await fetch(`${base}/api/conversations/export.xlsx`, {
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
  });
  if (!res.ok) throw new Error('No se pudo generar el Excel');
  const disposition = res.headers.get('content-disposition') ?? '';
  const nombre = /filename="([^"]+)"/.exec(disposition)?.[1] ?? 'crm-leads.xlsx';
  const url = URL.createObjectURL(await res.blob());
  const a = document.createElement('a');
  a.href = url;
  a.download = nombre;
  a.click();
  URL.revokeObjectURL(url);
}
